import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { UISection, UIShareStatus } from '../model/UiShapes';
import { defaultSection } from '../common/MetaData';
import { DEFAULT_SECTION_GUID } from '../common/MetaSetting';

// 内部状态接口，包含所有方法
interface SectionState {
  // State
  sections: UISection[];
  selectedSectionId: string;
  selectedSection: UISection | null;
  status: 'idle' | 'loading' | 'failed';
  error: string | null; // 统一的错误状态

  // Actions
  setSections: (sections: UISection[]) => void;
  addSection: (section: UISection) => void;
  setSelectedSectionById: (sectionId: string) => void;
  setCurrentSection: (section: UISection | null) => void;
  setStatus: (status: 'idle' | 'loading' | 'failed') => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  clear: () => void;
  updateSectionWithStatus: (section: UISection, updateStatus: number) => void;
  updateSection: (section: UISection) => void;
}

// 独立的actions对象
export const sectionStoreActions = {
  setSections: (set: any) => (sections: UISection[]) => {
    // 确保sections始终是数组类型
    if (!Array.isArray(sections)) {
      set({ sections: [] });
    } else {
      set({ sections });
    }
  },

  addSection: (set: any) => (section: UISection) =>
    set((state: SectionState) => ({
      sections: [...state.sections.filter((s) => s.guid !== section.guid), section]
    })),

  setSelectedSectionById: (set: any) => (sectionId: string) =>
    set((state: SectionState) => {
      // 只修改store中的值，不触发API调用
      let filteredSection: UISection | undefined = defaultSection;
      if (sectionId !== DEFAULT_SECTION_GUID) filteredSection = sectionId ? state.sections.find((s) => s.guid === sectionId) || undefined : undefined;
      return { selectedSectionId: sectionId, selectedSection: filteredSection };
    }),

  setCurrentSection: (set: any) => (section: UISection | null) => set({ selectedSection: section }),

  setStatus: (set: any) => (status: 'idle' | 'loading' | 'failed') => set({ status }),

  setError: (set: any) => (error: string | null) => set({ error }),

  clearError: (set: any) => () => set({ error: null }),

  clear: (set: any) => () =>
    set({
      sections: [],
      selectedSectionId: '',
      selectedSection: null,
      status: 'idle',
      error: null
    }),

  updateSectionWithStatus: (set: any, get: any) => (section: UISection, updateStatus: number) => {
    if (section.guid) {
      const currentState = get();
      set({
        sections: currentState.sections.map((s: UISection) => (s.guid === section.guid ? { ...section, updateStatus } : s))
      });
    }
  },

  // 更新section的同步操作（不包含异步逻辑）
  updateSection: (set: any) => (section: UISection) => {
    set({ status: 'loading' });

    // 更新本地状态
    set((state: SectionState) => {
      return {
        sections: state.sections.map((s) => (s.guid === section.guid ? section : s))
      };
    });

    set({ status: 'idle' });
  }
};

// 纯状态管理，移除异步操作
export const useSectionStore = create<SectionState>()(
  devtools(
    (set, get) => ({
      // Initial state
      sections: [],
      selectedSectionId: '',
      selectedSection: null,
      status: 'idle',
      error: null, // 错误状态初始化为null

      // Actions
      setSections: (sections: UISection[]) => sectionStoreActions.setSections(set)(sections),
      addSection: (section: UISection) => sectionStoreActions.addSection(set)(section),
      setSelectedSectionById: (sectionId: string) => sectionStoreActions.setSelectedSectionById(set)(sectionId),
      setCurrentSection: (section: UISection | null) => sectionStoreActions.setCurrentSection(set)(section),
      setStatus: (status: 'idle' | 'loading' | 'failed') => sectionStoreActions.setStatus(set)(status),
      setError: (error: string | null) => sectionStoreActions.setError(set)(error),
      clearError: () => sectionStoreActions.clearError(set)(),
      clear: () => sectionStoreActions.clear(set)(),
      updateSectionWithStatus: (section: UISection, updateStatus: number) => sectionStoreActions.updateSectionWithStatus(set, get)(section, updateStatus),
      updateSection: (section: UISection) => sectionStoreActions.updateSection(set)(section)
    }),
    {
      name: 'section-store'
    }
  )
);
